import {
  DISPATCH_ACKNOWLEDGED_KEY_PREFIX,
  buildSendOperationOwnerKey,
  dispatchAcknowledgedStorageKey,
  draftMessageStorageKey,
  legacySendOperationStorageKey,
  sendOperationStorageKey,
  type SendOperationKind,
} from 'utils/organizationStorage';

// 組織名前空間導入前の旧ドラフトキー（全ユーザー・全組織で共有）。
// 裸の 'constants' 指定子は Node 組み込みモジュールに解決されるため使わない。
const LEGACY_DRAFT_MESSAGE_KEY = 'draft_message';

type LegacyMigrationMembership = { organizationId: number };

const SEND_OPERATION_KINDS: readonly SendOperationKind[] = ['bulk', 'test'];

type PersistedSendOperationIdentity = { ownerKey?: unknown };

const migratedUids = new Set<string>();

// 1キーの失敗（容量超過など）で起動を止めない。移せなかったキーは次のセッションで再試行される
function tryStorage(operation: () => void): void {
  try {
    operation();
  } catch {
    return;
  }
}

function readJson(storage: Storage, key: string): unknown {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

/**
 * 組織スコープ導入前のグローバル localStorage キーを破棄または移行する。
 * 冪等（uid ごとに1セッション1回、移行後は旧キーが存在しない）。
 */
export function runLegacyOrganizationStorageMigration(
  uid: string,
  memberships: LegacyMigrationMembership[],
): void {
  if (migratedUids.has(uid)) return;
  migratedUids.add(uid);

  let storage: Storage;
  try {
    storage = window.localStorage;
  } catch {
    return;
  }

  const soleOrganizationId =
    memberships.length === 1 ? memberships[0].organizationId : null;

  // 旧ドラフトは組織を持たないため、移し先を一意に決められる単一所属のときだけ引き継ぐ
  tryStorage(() => {
    const legacyDraft = storage.getItem(LEGACY_DRAFT_MESSAGE_KEY);
    if (legacyDraft !== null && soleOrganizationId !== null) {
      const key = draftMessageStorageKey(uid, soleOrganizationId);
      if (storage.getItem(key) === null) {
        storage.setItem(key, legacyDraft);
      }
    }
    storage.removeItem(LEGACY_DRAFT_MESSAGE_KEY);
  });
  // 旧 dispatch acknowledged は回収の抑止リストなので所属の新キーへ引き継ぐ（消すと直近の同じ内容の再送が「回復」扱いになる）
  tryStorage(() => {
    const legacyAcknowledged = storage.getItem(DISPATCH_ACKNOWLEDGED_KEY_PREFIX);
    if (legacyAcknowledged !== null) {
      for (const { organizationId } of memberships) {
        const key = dispatchAcknowledgedStorageKey(uid, organizationId);
        if (storage.getItem(key) === null) {
          storage.setItem(key, legacyAcknowledged);
        }
      }
    }
    storage.removeItem(DISPATCH_ACKNOWLEDGED_KEY_PREFIX);
  });

  // 送信操作 identity はログイン中 UID のものだけ、単一所属なら新 ownerKey へ移す
  for (const kind of SEND_OPERATION_KINDS) {
    const legacyKey = legacySendOperationStorageKey(kind);
    const identity = readJson(storage, legacyKey) as PersistedSendOperationIdentity | null;
    if (!identity || typeof identity.ownerKey !== 'string') continue;
    // 所有者を証明できない値（別 UID・旧形式）は引き継がない
    if (identity.ownerKey !== uid) continue;
    // 所属が一意でなければ移し先を確定できない。消すと未確認送信の錨を失うので残す
    if (soleOrganizationId === null) continue;
    const ownerKey = buildSendOperationOwnerKey(uid, soleOrganizationId);
    // 新キーへ書けなかったときは旧キーを消さない（setItem が投げると removeItem に進まない）
    tryStorage(() => {
      storage.setItem(
        sendOperationStorageKey(kind, ownerKey),
        JSON.stringify({ ...identity, ownerKey }),
      );
      // 移し終えた旧キーは消す（残すと毎セッション同じ identity を移し続ける）
      storage.removeItem(legacyKey);
    });
  }
}

/** テスト用の内部状態リセット */
export function resetLegacyMigrationForTesting(): void {
  migratedUids.clear();
}
